
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, X, Package, Star } from 'lucide-react';
import { mockSavedProducts } from '@/api/mockData';
import type { OrderProduct, SavedProduct } from '@/types';

interface ProductManagerProps {
  products: OrderProduct[];
  onProductsChange: (products: OrderProduct[]) => void;
}

const ProductManager: React.FC<ProductManagerProps> = ({ products, onProductsChange }) => {
  const [showSavedProducts, setShowSavedProducts] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    value: '',
    weight: '',
    quantity: '1'
  });

  const addProduct = () => {
    if (newProduct.name && newProduct.value && newProduct.weight) {
      const product: OrderProduct = {
        id: `p${Date.now()}`,
        name: newProduct.name,
        value: parseInt(newProduct.value),
        weight: parseFloat(newProduct.weight),
        quantity: parseInt(newProduct.quantity) || 1
      };
      
      onProductsChange([...products, product]);
      setNewProduct({ name: '', value: '', weight: '', quantity: '1' });
    }
  };

  const updateProduct = (id: string, field: keyof OrderProduct, value: string | number) => {
    const updatedProducts = products.map(product => {
      if (product.id === id) {
        return { ...product, [field]: value };
      }
      return product;
    });
    onProductsChange(updatedProducts);
  };

  const removeProduct = (id: string) => {
    onProductsChange(products.filter(p => p.id !== id));
  };

  const addSavedProduct = (savedProduct: SavedProduct) => {
    const product: OrderProduct = {
      id: `p${Date.now()}`,
      name: savedProduct.name,
      value: savedProduct.defaultValue,
      weight: savedProduct.defaultWeight,
      quantity: 1
    };
    
    onProductsChange([...products, product]);
    setShowSavedProducts(false);
  };

  const totalValue = products.reduce((sum, p) => sum + (p.value * p.quantity), 0);
  const totalWeight = products.reduce((sum, p) => sum + (p.weight * p.quantity), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="font-bold text-pink-900 dark:text-pink-100">Danh sách sản phẩm</Label>
        <Dialog open={showSavedProducts} onOpenChange={setShowSavedProducts}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Package className="h-4 w-4 mr-2" />
              Chọn từ danh sách có sẵn
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Sản phẩm đã lưu</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {mockSavedProducts.map((savedProduct) => (
                <Card 
                  key={savedProduct.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => addSavedProduct(savedProduct)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-sm">{savedProduct.name}</h4>
                      {savedProduct.isFrequent && (
                        <Star className="h-3 w-3 text-yellow-500 fill-current" />
                      )}
                    </div>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div>Giá trị: {savedProduct.defaultValue.toLocaleString()}đ</div>
                      <div>Cân nặng: {savedProduct.defaultWeight}g</div>
                      <Badge variant="outline" className="text-xs">
                        {savedProduct.category}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Add new product form */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <Input
              placeholder="Tên sản phẩm"
              value={newProduct.name}
              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Giá trị (VNĐ)"
              value={newProduct.value}
              onChange={(e) => setNewProduct({ ...newProduct, value: e.target.value })}
            />
            <Input
              type="number"
              step="0.1"
              placeholder="Cân nặng (g)"
              value={newProduct.weight}
              onChange={(e) => setNewProduct({ ...newProduct, weight: e.target.value })}
            />
            <div className="flex gap-2">
              <Input
                type="number"
                min="1"
                placeholder="SL"
                value={newProduct.quantity}
                onChange={(e) => setNewProduct({ ...newProduct, quantity: e.target.value })}
                className="w-16"
              />
              <Button onClick={addProduct} size="sm" className="flex-1">
                <Plus className="h-4 w-4 mr-1" />
                Thêm
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products list */}
      {products.length > 0 && (
        <div className="space-y-2">
          {products.map((product) => (
            <Card key={product.id}>
              <CardContent className="p-3">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                  {/* Tên sản phẩm */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Tên sản phẩm</Label>
                    <Input
                      value={product.name}
                      onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                      className="h-8 text-sm"
                      placeholder="Tên sản phẩm"
                    />
                  </div>
                  
                  {/* Số lượng */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Số lượng</Label>
                    <Input
                      type="number"
                      min="1"
                      value={product.quantity}
                      onChange={(e) => updateProduct(product.id, 'quantity', parseInt(e.target.value) || 1)}
                      className="h-8 text-sm"
                      placeholder="SL"
                    />
                  </div>
                  
                  {/* Giá tiền */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Giá trị (VNĐ)</Label>
                    <Input
                      type="number"
                      value={product.value}
                      onChange={(e) => updateProduct(product.id, 'value', parseInt(e.target.value) || 0)}
                      className="h-8 text-sm"
                      placeholder="Giá trị"
                    />
                  </div>
                  
                  {/* Khối lượng */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Cân nặng (g)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={product.weight}
                      onChange={(e) => updateProduct(product.id, 'weight', parseFloat(e.target.value) || 0)}
                      className="h-8 text-sm"
                      placeholder="Cân nặng"
                    />
                  </div>
                  
                  {/* Tổng và nút xóa */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Thành tiền</Label>
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <div className="font-medium">{(product.value * product.quantity).toLocaleString()}đ</div>
                        <div className="text-xs text-muted-foreground">{(product.weight * product.quantity)}g</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeProduct(product.id)}
                        className="text-destructive hover:text-destructive h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Summary */}
          <Card className="bg-muted/50">
            <CardContent className="p-3">
              <div className="flex justify-between text-sm">
                <span>Tổng cộng:</span>
                <div className="text-right">
                  <div className="font-medium">{totalValue.toLocaleString()}đ</div>
                  <div className="text-muted-foreground">{totalWeight}g</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
